// ============================================================================
// Imports
// ============================================================================
import api from './api';
import { TableConfig } from '../types';
import { AxiosError } from 'axios';

// ============================================================================
// Types & Interfaces
// ============================================================================
export interface Application {
  id: number;
  name: string;
  display_name: string;
  description?: string;
  icon?: string;
  sort_order: number;
  show_in_sidebar?: boolean;
}

export interface FormTable {
  id: number;
  table_name: string;
  label: string;
  description?: string;
  category?: string;
  application_id: number;
  visible: boolean;
  icon?: string;
  sort_order: number;
}

// ============================================================================
// API Methods
// ============================================================================
export async function getApplications(): Promise<Application[]> {
  try {
    const response = await api.get('/applications');
    return response.data;
  } catch (error) {
    console.error('[menuService] Error fetching applications:', error);
    return [];
  }
}

export async function getFormTables(): Promise<FormTable[]> {
  try {
    const response = await api.get('/config');
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      if (error.response?.status === 404) {
        console.warn('[menuService] Table configuration not found - some tables may have been removed');
      } else {
        console.error('[menuService] Error fetching form tables:', error.message);
      }
    } else {
      console.error('[menuService] Error fetching form tables:', error);
    }
    return [];
  }
}

// ============================================================================
// Menu Generation
// ============================================================================
export async function getDynamicMenuItems() {
  try {
    // Fetch applications and form tables in parallel
    const [applicationsResponse, formTablesResponse] = await Promise.all([
      api.get('/applications'),
      api.get('/config')
    ]);

    // Handle applications response - it's a direct array, not paginated
    const applications: Application[] = applicationsResponse.data || [];
    const formTables: FormTable[] = Array.isArray(formTablesResponse.data) ? formTablesResponse.data : [];

    // Filter tables to only show those with visible flag set to true
    const visibleTables = formTables.filter(table => table.visible);
    
    // Group tables by their application
    const tablesByApp: Record<number, FormTable[]> = {};
    
    visibleTables.forEach(table => {
      if (!tablesByApp[table.application_id]) {
        tablesByApp[table.application_id] = [];
      }
      tablesByApp[table.application_id].push({
        ...table,
        label: table.label || table.table_name,
        icon: table.icon || 'table'
      });
    });
    
    // Sort applications by sort_order
    const sortedApplications = [...applications].sort((a, b) => a.sort_order - b.sort_order);
    
    // Sort tables within each application by sort_order
    Object.keys(tablesByApp).forEach(appId => {
      const numAppId = parseInt(appId, 10);
      if (tablesByApp[numAppId] && Array.isArray(tablesByApp[numAppId])) {
        tablesByApp[numAppId] = tablesByApp[numAppId].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      }
    });
    
    return {
      applications: sortedApplications,
      tablesByApp
    };
  } catch (error) {
    if (error instanceof AxiosError) {
      if (error.response?.status === 404) {
        console.warn('[menuService] Some menu items could not be loaded - tables may have been removed');
      } else {
        console.error('[menuService] Error fetching dynamic menu items:', error.message);
      }
    } else {
      console.error('[menuService] Error fetching dynamic menu items:', error);
    }
    return {
      applications: [],
      tablesByApp: {}
    };
  }
}
